import { useState } from "react";

import { useI18n } from "~/i18n";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

/**
 * Name a folder. Serves both creating (empty initial name) and renaming (seeded name, different
 * title/button) — same small form, so there's one place that validates a folder name. The caller
 * remounts it with a `key` when `initialName` changes, so the seeded value is always fresh.
 */
export function VaultCreateFolderDialog({
  open,
  onOpenChange,
  onCreate,
  initialName = "",
  title,
  submitLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the new/renamed folder name. */
  onCreate: (name: string) => void;
  /** Seed value for rename; omit (or "") for create. */
  initialName?: string;
  /** Override the dialog title (rename vs create). */
  title?: string;
  /** Override the submit button label (rename vs create). */
  submitLabel?: string;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(initialName);

  const trimmed = name.trim();
  const isRename = initialName.length > 0;
  // Nothing to submit on an empty name, and on rename an unchanged name is a no-op too.
  const canSubmit = trimmed.length > 0 && trimmed !== initialName.trim();

  const submit = () => {
    if (!canSubmit) return;
    onCreate(trimmed);
    setName(initialName);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) setName(initialName);
      }}
    >
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>
            {title ?? (isRename ? t("vault.renameFolderTitle") : t("vault.createFolderTitle"))}
          </DialogTitle>
          {isRename ? null : <DialogDescription>{t("vault.createFolderHint")}</DialogDescription>}
        </DialogHeader>
        <DialogPanel className="space-y-4">
          <label className="block space-y-2">
            <span className="text-sm font-medium text-foreground">{t("vault.folderName")}</span>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("vault.folderNamePlaceholder")}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  submit();
                }
              }}
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost-muted" onClick={() => onOpenChange(false)}>
              {t("vault.createFolderCancel")}
            </Button>
            <Button type="button" disabled={!canSubmit} onClick={submit}>
              {submitLabel ?? (isRename ? t("vault.rename") : t("vault.createFolder"))}
            </Button>
          </div>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
